import { useConfirmTwoFA, useDeleteTwoFA, useSetTwoFA } from "@/api/mutations/auth";
import { useMe } from "@/api/queries/auth";
import { OtpConfirmationDialog } from "@/components/otp-confirmation-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { AxiosError } from "axios";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const TwoFA = () => {
  const { data: me } = useMe();

  const twoFAIsEnabled = me?.data
    ? !!(me.data.twoFactorEnabled && !me.data.twoFactorPending)
    : false;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);

  const { mutateAsync: setTwoFAMutation, isPending: isSetting } = useSetTwoFA();

  const { mutateAsync: deleteTwoFAMutation, isPending: isDeleting } = useDeleteTwoFA();

  const { mutateAsync: confirmTwoFAMutation, isPending: isConfirming } = useConfirmTwoFA();

  const isLoading = isSetting || isDeleting || isConfirming;

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      setTwoFAMutation()
        .then((res) => {
          setQrCode(res.qrCode);
          setDialogOpen(true);
        })
        .catch((err: AxiosError<{ message: string }, unknown>) => {
          toast.error(err.response?.data?.["message"] ?? "Failed to setup two fa");
        });

      return;
    } else {
      setDialogOpen(true);
    }
  };

  const handleConfirm = async (otp: string) => {
    if (twoFAIsEnabled) {
      deleteTwoFAMutation({ otp })
        .then(() => {
          toast.success("Two-factor authentication has been disabled.");
          setDialogOpen(false);
        })
        .catch((err: AxiosError<{ message: string }, unknown>) => {
          toast.error(
            err.response?.data?.message ?? "Failed to disable two-factor authentication.",
          );
        });
    } else {
      confirmTwoFAMutation({ otp })
        .then(() => {
          toast.success("Two-factor authentication has been enabled.");
          setDialogOpen(false);
          setQrCode(null);
        })
        .catch((err: AxiosError<{ message: string }, unknown>) => {
          toast.error(err.response?.data?.message ?? "Failed to enable two-factor authentication.");
        });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <ShieldCheck className="size-5" />

            <div>
              <CardTitle>Two-factor authentication</CardTitle>
              <CardDescription>
                Protect your account by requiring a verification code from your authenticator app
                when signing in.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex items-center justify-between">
          <div>
            <Label htmlFor="two-fa" className="text-base">
              Enable two-factor authentication
            </Label>

            <p className="text-muted-foreground text-sm">
              {twoFAIsEnabled
                ? "Two-factor authentication is enabled."
                : "Two-factor authentication is disabled."}
            </p>
          </div>

          <Switch
            id="two-fa"
            checked={twoFAIsEnabled}
            onCheckedChange={handleToggle}
            disabled={isLoading}
          />
        </CardContent>
      </Card>

      <OtpConfirmationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleConfirm}
        isLoading={isConfirming || isDeleting}
      >
        {qrCode && (
          <div className="flex flex-col items-center gap-3 py-4">
            <p className="text-muted-foreground text-sm text-center">
              Scan this QR code with your authenticator app, then enter the generated 6-digit
              verification code below.
            </p>

            <img src={qrCode} alt="Two-factor authentication QR code" className="size-52" />
          </div>
        )}
      </OtpConfirmationDialog>
    </>
  );
};
